import React from 'react';
import { Pencil, Trash2 } from 'lucide-react';
import {
  Badge,
  Button,
  Checkbox,
  TableCell,
  TableRow,
} from '@roger/ui-components';
import QuickDiscontinueButton from './QuickDiscontinueButton';
import type { PatientMedicationResponse } from '../../services/api';
import {
  formatDate,
  getMedicationDisplayName,
  getMedicationDosageDisplay,
  isActuallyDiscontinued,
} from './medicationUtils';
import { parseISOToLocalDate } from '../../utils/dateUtils';

interface MedicationTableRowProps {
  medication: PatientMedicationResponse;
  onEdit: (medication: PatientMedicationResponse) => void;
  onDelete: (medication: PatientMedicationResponse) => void;
  onDiscontinue: (medicationId: string, date: Date) => Promise<void>;
  selection?: {
    selected: boolean;
    eligible: boolean;
    reason?: string;
    onToggle: (checked: boolean) => void;
  };
}

const MedicationTableRow: React.FC<MedicationTableRowProps> = ({
  medication: med,
  onEdit,
  onDelete,
  onDiscontinue,
  selection,
}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const isFuture = parseISOToLocalDate(med.start_date)! > today;
  const isDiscontinued = isActuallyDiscontinued(med);
  const isMuted = isFuture || isDiscontinued;

  const dosageDisplay = getMedicationDosageDisplay(med);

  return (
    <TableRow className={isMuted ? 'opacity-60' : undefined}>
      {selection && (
        <TableCell>
          <Checkbox
            checkboxSize='sm'
            checked={selection.selected}
            disabled={!selection.eligible}
            onCheckedChange={checked => selection.onToggle(Boolean(checked))}
            aria-label={`Select ${getMedicationDisplayName(med)}`}
          />
        </TableCell>
      )}
      <TableCell>
        <div className='flex flex-col gap-1'>
          {isFuture ? (
            <Badge variant='secondary' className='text-xs w-fit'>
              Future
            </Badge>
          ) : isDiscontinued ? (
            <Badge variant='secondary' className='text-xs w-fit'>
              Discontinued
            </Badge>
          ) : (
            med.is_longstanding && (
              <Badge variant='outline' className='text-xs w-fit'>
                Longstanding
              </Badge>
            )
          )}
        </div>
      </TableCell>
      <TableCell className='text-sm'>{formatDate(med.start_date)}</TableCell>
      <TableCell className='text-sm'>
        {selection ? (
          med.discontinue_date ? (
            <span className='text-muted-foreground'>
              {formatDate(med.discontinue_date)}
            </span>
          ) : (
            <span className='text-sm text-muted-foreground'>Active</span>
          )
        ) : med.discontinue_date ? (
          <span className='text-muted-foreground'>
            {formatDate(med.discontinue_date)}
          </span>
        ) : (
          <QuickDiscontinueButton
            minDate={parseISOToLocalDate(med.start_date)!}
            onDiscontinue={date => onDiscontinue(med.id, date)}
          />
        )}
      </TableCell>
      <TableCell>
        <div>
          <div className='font-medium'>{getMedicationDisplayName(med)}</div>
          {(dosageDisplay || med.frequency_instructions) && (
            <div className='text-sm text-muted-foreground mt-0.5'>
              {dosageDisplay}
              {dosageDisplay && med.frequency_instructions && ' - '}
              {med.frequency_instructions}
            </div>
          )}
          {selection?.reason && (
            <div className='text-xs text-muted-foreground mt-1'>
              {selection.reason}
            </div>
          )}
        </div>
      </TableCell>
      <TableCell>
        {selection ? (
          <span className='sr-only'>Medication selection mode</span>
        ) : (
          <div className='flex items-center gap-1'>
            <Button variant='ghost' size='sm' onClick={() => onEdit(med)}>
              <Pencil className='h-4 w-4' />
            </Button>
            <Button variant='ghost' size='sm' onClick={() => onDelete(med)}>
              <Trash2 className='h-4 w-4 text-destructive' />
            </Button>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
};

export default MedicationTableRow;
